/**
 * O histórico de manutenção da frota, em tabela.
 *
 * Papel: mostra uma linha por manutenção, com o veículo, a data, a
 * quilometragem, o tipo de serviço, a descrição, a oficina, o valor e a
 * próxima revisão.
 *
 * Como usar: quem monta a tabela passa `onEditar` e `onRemover` apenas para
 * quem tem nível `editar`; sem eles a coluna de ações some inteira.
 *
 * O veículo aparece pelo código, pela placa e pelo modelo, e os três vêm do
 * cadastro — não há cópia deles na manutenção. É o que impede a placa mostrada
 * aqui de divergir da que está no cadastro do carro.
 *
 * Duas manutenções do mesmo veículo são duas linhas distinguíveis, cada uma com
 * o seu botão: o identificador é da manutenção, e não do carro.
 */
import { type ManutencaoVeiculo } from '@concierge/shared';

import {
  formatarData,
  formatarDinheiro,
  formatarInteiro,
  formatarPlaca,
} from '@/shared/lib/formato';
import { Button } from '@/shared/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';

interface TabelaManutencoesProps {
  manutencoes: ManutencaoVeiculo[];
  /** Chamado com a manutenção da linha; ausente esconde a coluna de ações. */
  onEditar?: (manutencao: ManutencaoVeiculo) => void;
  /** Chamado com a manutenção a excluir; a confirmação é de quem monta. */
  onRemover?: (manutencao: ManutencaoVeiculo) => void;
}

/**
 * A tabela do histórico.
 *
 * @param manutencoes - As manutenções a mostrar, já em ordem.
 * @param onEditar - Chamado ao clicar em editar; ausente esconde as ações.
 * @param onRemover - Chamado ao clicar em remover; ausente esconde as ações.
 * @returns A tabela com uma linha por manutenção.
 */
export function TabelaManutencoes({
  manutencoes,
  onEditar,
  onRemover,
}: TabelaManutencoesProps) {
  const mostrarAcoes = onEditar !== undefined || onRemover !== undefined;

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>ID</TableHead>
          <TableHead>Placa</TableHead>
          <TableHead>Modelo</TableHead>
          <TableHead>Data</TableHead>
          <TableHead>KM atual</TableHead>
          <TableHead>Tipo de serviço</TableHead>
          <TableHead>Descrição</TableHead>
          <TableHead>Oficina/Fornecedor</TableHead>
          <TableHead>Valor</TableHead>
          <TableHead>Próx. manutenção</TableHead>
          <TableHead>Próx. KM</TableHead>
          {mostrarAcoes && (
            <TableHead>
              <span className="sr-only">Ações</span>
            </TableHead>
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {manutencoes.map((manutencao) => (
          <TableRow key={manutencao.id}>
            <TableCell className="tabular">{manutencao.veiculo.codigo}</TableCell>
            <TableCell className="tabular">
              {formatarPlaca(manutencao.veiculo.placa)}
            </TableCell>
            <TableCell className="text-texto-medio">{manutencao.veiculo.modelo}</TableCell>
            <TableCell className="tabular">{formatarData(manutencao.data)}</TableCell>
            <TableCell className="tabular">{formatarInteiro(manutencao.km)}</TableCell>
            <TableCell>{manutencao.tipo}</TableCell>
            <TableCell className="text-texto-medio">{manutencao.descricao ?? '—'}</TableCell>
            <TableCell>{manutencao.oficina ?? '—'}</TableCell>
            <TableCell className="tabular">{formatarDinheiro(manutencao.valor)}</TableCell>
            <TableCell className="tabular">{formatarData(manutencao.proxData)}</TableCell>
            <TableCell className="tabular">{formatarInteiro(manutencao.proxKm)}</TableCell>
            {mostrarAcoes && (
              <TableCell>
                <div className="flex gap-2">
                  {onEditar !== undefined && (
                    <Button variant="outline" size="sm" onClick={() => onEditar(manutencao)}>
                      editar
                    </Button>
                  )}
                  {onRemover !== undefined && (
                    <Button variant="destructive" onClick={() => onRemover(manutencao)}>
                      remover
                    </Button>
                  )}
                </div>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
